import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Warehouse, PackageOpen, ArrowDownCircle, AlertTriangle } from 'lucide-react';
import { StatCard, WidgetPanel } from './DashboardShared';
import { warehouses, stockRows, stockTransfers } from '../../../data/store';

export const WarehouseModule = ({ org }) => {
  const metrics = useMemo(() => {
    const orgWarehouses = warehouses.filter(w => w.orgId === org.id);
    const whIds = orgWarehouses.map(w => w.id);
    
    let stockValue = 0;
    const lowStock = [];
    const whStockMap = {}; // whId -> stockValue
    
    orgWarehouses.forEach(w => {
       whStockMap[w.id] = 0;
    });

    stockRows.forEach(r => {
      if (r.orgId === org.id && whIds.includes(r.locationId)) {
        if (r.status === 'active') {
          const qty = Number(r.qty) || 0;
          const val = qty * (Number(r.costPrice) || 0);
          stockValue += val;
          if (whStockMap[r.locationId] !== undefined) {
             whStockMap[r.locationId] += val;
          }
          
          const threshold = Number(r.minStockThreshold) || 0;
          if (threshold > 0 && qty <= threshold) {
            lowStock.push(r);
          }
        }
      }
    });

    const orgTransfers = stockTransfers.filter(t => t.orgId === org.id);
    let pendingRequests = 0;
    orgTransfers.forEach(t => {
      if (t.status === 'requested' && whIds.includes(t.fromId)) {
        pendingRequests++;
      }
    });

    return { totalWarehouses: orgWarehouses.length, stockValue, pendingRequests, lowStock };
  }, [org.id]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.15em] text-[#0d0d0d] border-b border-slate-100 pb-3 mt-4">
        <Warehouse size={16} className="text-[var(--theme-primary)]" />
        Warehouse Operations
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={Warehouse} label="Total Warehouses" value={metrics.totalWarehouses} iconColor="#8b5cf6" />
        <StatCard icon={PackageOpen} label="Central Stock Value" value={`₹${metrics.stockValue.toFixed(2)}`} iconColor="#059669" />
        <StatCard icon={ArrowDownCircle} label="Pending Sourcing Reqs" value={metrics.pendingRequests} iconColor="#f59e0b" />
      </div>

      <div className="grid grid-cols-1 gap-6">
        {metrics.lowStock.length > 0 ? (
          <WidgetPanel title="Low Stock Alerts" count={metrics.lowStock.length} className="!border-orange-100 bg-orange-50/10">
            <ul className="divide-y divide-orange-50 mt-2">
              {metrics.lowStock.slice(0, 5).map((item, idx) => (
                <li key={idx} className="py-3 flex items-center justify-between text-xs">
                  <span className="font-bold text-slate-700 truncate pr-4">{item.productId}</span>
                  <span className="font-bold text-orange-600 bg-orange-100 px-3 py-1 rounded-lg shrink-0">{item.qty} left</span>
                </li>
              ))}
            </ul>
          </WidgetPanel>
        ) : (
           <WidgetPanel title="Warehouse Health">
              <div className="flex-1 flex items-center justify-center text-center text-slate-400">
                 <p className="text-xs font-bold uppercase tracking-widest">All stock levels normal</p>
              </div>
           </WidgetPanel>
        )}
      </div>
    </div>
  );
};

WarehouseModule.propTypes = {
  org: PropTypes.object.isRequired,
};
